/**
 * design_handoff_billing — the Billing tab of Workspace settings. Card 1
 * (subscription) + Card 2 (plan usage) side by side; the account-not-
 * activated gate replaces both entirely. "Change plan" is its own route,
 * see pages/ChangePlanPage.js.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import TabbedSettingsPage from "../templates/TabbedSettingsPage/TabbedSettingsPage";
import { WORKSPACE_SETTINGS_TABS } from "../templates/TabbedSettingsPage/settingsTabs";
import PlanSummaryCard from "./BillingPage/PlanSummaryCard";
import SubscriptionLimits from "../component/InfoWidgets/SubscriptionLimits";
import ActivationGate from "./BillingPage/ActivationGate";
import { useUserStore } from "../../../hooks/redux/user";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useSubscriptionStore } from "../../../hooks/redux/subscription";
import styles from "./BillingPage.module.scss";

const CHECKOUT_POLL_INTERVAL_MS = 3000;
const CHECKOUT_POLL_MAX_ATTEMPTS = 8;

const BillingPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, checkSession, resendActivationToken } = useUserStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { subscription, fetchCustomerPortalLink } = useSubscriptionStore();

  const isFetchingLink =
    subscription.stripeBillingPortalLinkFetch || subscription.stripeCustomerSessionFetch;

  // True right after landing back from Stripe Checkout (see `?checkout=`
  // marker on success_url) until either the subscription actually changes
  // or CHECKOUT_POLL_MAX_ATTEMPTS is exhausted. A plain trial/active
  // activation never sets the DB's own pending_payment flag (that's only
  // set for the "requires action" case), so without this the page would
  // keep showing pre-checkout data until a manual reload.
  const [checkingOut, setCheckingOut] = useState(false);
  const checkoutBaseline = useRef(null);

  useEffect(() => {
    if (!new URLSearchParams(location.search).has("checkout")) {
      return;
    }
    checkoutBaseline.current = JSON.stringify(currentOrganization.subscription);
    setCheckingOut(true);
    history.replace(location.pathname);
    // Intentionally read the return URL once, on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!checkingOut) {
      return;
    }
    if (JSON.stringify(currentOrganization.subscription) !== checkoutBaseline.current) {
      setCheckingOut(false);
    }
  }, [checkingOut, currentOrganization.subscription]);

  useEffect(() => {
    if (!checkingOut) {
      return undefined;
    }
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      checkSession();
      if (attempts >= CHECKOUT_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
        setCheckingOut(false);
      }
    }, CHECKOUT_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [checkingOut, checkSession]);

  useEffect(() => {
    if (subscription.stripeBillingPortalLinkFetchSuccess && subscription.stripeBillingPortalLink) {
      window.open(subscription.stripeBillingPortalLink.url, "_self");
    }
  }, [subscription.stripeBillingPortalLinkFetchSuccess]);

  // Pending-payment webhook can land any time after checkout — poll session
  // while it's outstanding so the page resolves itself without a reload.
  useEffect(() => {
    const pending = currentOrganization.model?.pending_payment * 1;
    if (!pending) return undefined;
    const timer = setInterval(() => checkSession(), 5000);
    return () => clearInterval(timer);
  }, [currentOrganization.model?.pending_payment]);

  const goToPlans = useCallback(() => {
    history.push("/organization-settings/billing/plans");
  }, [history]);

  const onResendActivation = useCallback(
    (e) => {
      e?.preventDefault?.();
      resendActivationToken();
    },
    [resendActivationToken]
  );

  const cardActions = {
    changePlan: goToPlans,
    renew: goToPlans,
    manageSubscription: () => fetchCustomerPortalLink(),
    updateCard: () => fetchCustomerPortalLink(),
    editBillingEmail: () => fetchCustomerPortalLink(),
    paymentHistory: () => fetchCustomerPortalLink(),
    cancelSubscription: () => fetchCustomerPortalLink("subscription_cancel"),
    cancelTrial: () => fetchCustomerPortalLink("subscription_cancel"),
    // Per product decision: the account's login email is only editable via
    // the Stripe customer portal today — no dedicated change-email flow
    // exists in this app, and the intent is to keep this page from owning
    // anything Stripe already handles.
    changeEmail: () => fetchCustomerPortalLink(),
  };

  const isAccountVerified = Boolean(user.model?.is_account_verified);
  const organizationModel = currentOrganization.model;

  return (
    <TabbedSettingsPage
      title="Workspace settings"
      subtitle={
        organizationModel?.name &&
        `Applies to ${organizationModel.name}. Other workspaces are unaffected.`
      }
      tabs={WORKSPACE_SETTINGS_TABS}
    >
      {isAccountVerified ? (
        <div className={styles.grid}>
          <PlanSummaryCard
            actions={cardActions}
            disabled={isFetchingLink || checkingOut}
            checkingOut={checkingOut}
          />
          <SubscriptionLimits title="Plan usage" splitFooter showManageButton={false} />
        </div>
      ) : (
        <ActivationGate
          email={user.model?.email}
          disabled={user.resendActivation}
          onResend={onResendActivation}
          onChangeEmail={cardActions.changeEmail}
        />
      )}
    </TabbedSettingsPage>
  );
};

export default BillingPage;
